import type {ReactElement} from 'react';
import React from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {ClickableDiv} from '@Components/clickable-div';
import {SkeletonBrick, SkeletonBrickTheme} from '@Components/skeleton-brick';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import styles from './bottom-bar-item.module.scss';

export interface BottomBarItemProps {
  id: string;
  className?: string;

  onClick?(e: React.MouseEvent): void;

  icon?: string;
  iconClasses?: string;
  textClasses?: string;
  selectedClasses?: string;
  customContent?: ReactElement<any>;
  text: string;
  isHidden?: boolean;
  showSkeleton?: boolean;
  tag?: ReactElement<any> | null;
  isLoading?: boolean;
  isTextBold?: boolean;
  loadingText?: string;
  isDisabled?: boolean;
  isSelected?: boolean;
  isAiOption?: boolean;
  customNotification?: ReactElement<any>;
  showCustomContentWithIcon?: boolean;
}

export function BottomBarItem({
  className = '',
  tag = undefined,
  showSkeleton = false,
  icon = '',
  iconClasses = '',
  onClick = (): void => {},
  isHidden = false,
  isLoading = false,
  loadingText = '',
  isTextBold = false,
  isDisabled = false,
  isSelected = false,
  isAiOption = false,
  textClasses = '',
  selectedClasses = '',
  customNotification = undefined,
  showCustomContentWithIcon = false,
  ...props
}: BottomBarItemProps): ReactElement<any> | null {
  if (isHidden && !showSkeleton) {
    return null;
  }

  const getIcon = (): ReactElement => {
    return <Icon icon={icon} className={`${iconClasses} ${isAiOption ? 'content-ai' : ''}`} size={IconSize.SIZE_ICON_24} />;
  };

  const getInnerContent = (): ReactElement<any> => {
    if (isLoading) {
      return (
        <CircularProgressLoader
          classes={`${isAiOption ? 'spacing-m-1' : 'spacing-m-0'}`}
          size={isAiOption ? LOADER_SIZE.SMALL : LOADER_SIZE.DEFAULT}
          color={isAiOption ? LOADER_COLOR.AI : LOADER_COLOR.DARK}
        />
      );
    }

    return props.customContent ? (
      <div className="flex-center">
        {props.customContent}
        {showCustomContentWithIcon ? getIcon() : null}
      </div>
    ) : (
      getIcon()
    );
  };

  const getContent = (): ReactElement<any> => {
    if (showSkeleton) {
      return (
        <>
          <SkeletonBrick theme={SkeletonBrickTheme.LIGHT_TWO} width={24} height={24} radius="4px" />
          <SkeletonBrick theme={SkeletonBrickTheme.LIGHT_TWO} width={50} className="spacing-m-t-2" height={12} radius="4px" />
        </>
      );
    }
    return (
      <>
        {tag ? <div className={styles.tag}>{tag}</div> : null}
        {getInnerContent()}
        <Text
          val={isLoading ? (loadingText ?? window.i18next.t('pmwjs_loading')) : props.text}
          size={TextSize.XXSMALL}
          bold={isTextBold}
          className={`${styles.text} ${textClasses} ${isAiOption ? 'content-ai' : ''}`}
        />
      </>
    );
  };

  const getSelectedStateClasses = (): string => {
    if (isSelected) {
      return selectedClasses === '' ? styles.isSelected : selectedClasses;
    }
    return '';
  };

  return (
    <>
      <ClickableDiv
        onClick={(e): void => {
          if (isDisabled) {
            return;
          }
          onClick(e);
        }}
        className={`${styles.item} flex-1 ${className} ${isDisabled ? styles.isDisabled : ''} ${getSelectedStateClasses()} ${
          isLoading && !isDisabled ? styles.loading : ''
        } flex-v-row radius-4 flex-items-center ${showSkeleton ? styles.skeleton : ''}`}
      >
        {getContent()}
      </ClickableDiv>
      {customNotification}
    </>
  );
}
